import { FC, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from "@mui/material";
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  ExpandMore as ExpandMoreIcon,
} from "@mui/icons-material";
import type { TFeeRuleType } from "../../../apis/feeStructure/feeStructure.interfaces";
import type { TFeeType } from "../../../apis/feeStructure/feeStructure.interfaces";
import {
  useClassFeesHook,
  useFeeComponentsHook,
  useStudentCategoriesForClassHook,
  useCategoryAttachmentsHook,
  useCreateFeeComponentHook,
  useCreateStudentCategoryHook,
  useDeleteStudentCategoryHook,
  useCreateCategoryAttachmentHook,
  useDeleteCategoryAttachmentHook,
  useDeleteFeeComponentHook,
} from "../../../hooks/useFeeStructureHook";
import { formatAmountWithCurrency } from "../feeStructure/feeStructure.helpers";
import ControlledTextField from "../../components/controlledComponents/ControlledTextField";
import ControlledSelectField from "../../components/controlledComponents/ControlledSelectField";
import { logger } from "../../../utils/common";

const RULE_TYPES: { key: TFeeRuleType; value: string }[] = [
  { key: "override_amount", value: "Override amount" },
  { key: "waive", value: "Waive" },
  { key: "discount_fixed", value: "Discount (fixed)" },
  { key: "discount_percent", value: "Discount (%)" },
  { key: "surcharge_fixed", value: "Surcharge (fixed)" },
  { key: "surcharge_percent", value: "Surcharge (%)" },
];

const FEE_TYPES: { key: TFeeType; value: string }[] = [
  { key: "tuition", value: "Tuition" },
  { key: "admission", value: "Admission" },
  { key: "exam", value: "Exam" },
  { key: "library", value: "Library" },
  { key: "sports", value: "Sports" },
  { key: "transport", value: "Transport" },
  { key: "hostel", value: "Hostel" },
  { key: "other", value: "Other" },
];

const FREQUENCIES = [
  { key: "one-time", value: "One-time" },
  { key: "monthly", value: "Monthly" },
  { key: "quarterly", value: "Quarterly" },
  { key: "biannual", value: "Bi-annual" },
  { key: "annual", value: "Annual" },
];

type ClassFeeStructurePanelProps = {
  classId: string;
  academicYear?: string;
};

const CategoryAttachmentsSection: FC<{
  categoryId: string;
  classId: string;
}> = ({ categoryId, classId }) => {
  const { data: attachments, isLoading } = useCategoryAttachmentsHook(categoryId);
  const removeRule = useDeleteCategoryAttachmentHook();

  const handleDelete = async (ruleId: string) => {
    if (!window.confirm("Remove this fee line from the category?")) return;
    try {
      await removeRule.mutateAsync({ categoryId, ruleId, classId });
      logger.show_success("Fee attachment removed.");
    } catch (e) {
      logger.show_error(e instanceof Error ? e.message : "Remove failed.");
    }
  };

  if (isLoading) {
    return <CircularProgress size={22} />;
  }

  return (
    <TableContainer component={Paper} variant="outlined">
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>Fee</TableCell>
            <TableCell>Rule</TableCell>
            <TableCell align="right">Amount</TableCell>
            <TableCell align="right">Actions</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {(attachments ?? []).map((r) => (
            <TableRow key={r.id}>
              <TableCell>{r.componentType.name ?? r.componentType.code}</TableCell>
              <TableCell>{r.ruleType}</TableCell>
              <TableCell align="right">
                {r.ruleType === "waive"
                  ? "Waived"
                  : formatAmountWithCurrency(
                      Number(
                        r.value ?? r.componentType.amount ?? 0,
                      ),
                      r.componentType.currencyCode ?? "INR",
                    )}
              </TableCell>
              <TableCell align="right">
                <IconButton
                  size="small"
                  color="error"
                  aria-label="Remove attachment"
                  onClick={() => handleDelete(r.id)}
                >
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
      {(attachments ?? []).length === 0 ? (
        <Box sx={{ p: 2 }}>
          <Typography variant="body2" color="text.secondary">
            No fee components attached yet. Use &quot;Attach fee&quot; to link an
            institute fee component to this student category.
          </Typography>
        </Box>
      ) : null}
    </TableContainer>
  );
};

const ClassFeeStructurePanel: FC<ClassFeeStructurePanelProps> = ({
  classId,
  academicYear,
}) => {
  const { data: classFees, isLoading: cfLoading } = useClassFeesHook({
    classId,
    academicYear,
  });
  const { data: instituteComponents, isLoading: compLoading } =
    useFeeComponentsHook();
  const { data: studentCategories, isLoading: catLoading } =
    useStudentCategoriesForClassHook(classId);

  const createFeeComp = useCreateFeeComponentHook();
  const createStudentCat = useCreateStudentCategoryHook();
  const deleteStudentCat = useDeleteStudentCategoryHook();
  const deleteFeeComp = useDeleteFeeComponentHook();
  const createAttach = useCreateCategoryAttachmentHook();

  const [feeDialogOpen, setFeeDialogOpen] = useState(false);
  const [catDialogOpen, setCatDialogOpen] = useState(false);
  const [attachForCategory, setAttachForCategory] = useState<string | null>(null);

  const feeForm = useForm<{
    name: string;
    feeType: TFeeType;
    amount: number;
    frequency: string;
    description: string;
  }>({
    defaultValues: {
      name: "",
      feeType: "tuition",
      amount: 0,
      frequency: "monthly",
      description: "",
    },
  });

  const catForm = useForm<{ name: string; description: string }>({
    defaultValues: { name: "", description: "" },
  });

  const attachForm = useForm<{
    componentTypeId: string;
    ruleType: TFeeRuleType;
    value: string;
  }>({
    defaultValues: {
      componentTypeId: "",
      ruleType: "override_amount",
      value: "",
    },
  });

  const classBlock = useMemo(
    () => classFees?.classes?.find((c) => c.classId === classId),
    [classFees, classId],
  );

  const componentOptions = useMemo(
    () =>
      (instituteComponents ?? []).map((c) => ({
        key: c.id,
        value: `${c.name} (${c.feeType})`,
      })),
    [instituteComponents],
  );

  const saveFeeComponent = feeForm.handleSubmit(async (vals) => {
    if (!vals.name.trim()) {
      logger.show_error("Name is required.");
      return;
    }
    try {
      await createFeeComp.mutateAsync({
        name: vals.name.trim(),
        feeType: vals.feeType,
        amount: Number(vals.amount),
        frequency: vals.frequency as "one-time" | "monthly" | "quarterly" | "biannual" | "annual",
        description: vals.description.trim() || undefined,
        currency: "INR",
        status: "active",
      });
      logger.show_success("Fee component created.");
      setFeeDialogOpen(false);
      feeForm.reset();
    } catch (e) {
      logger.show_error(e instanceof Error ? e.message : "Could not create.");
    }
  });

  const saveStudentCategory = catForm.handleSubmit(async (vals) => {
    if (!vals.name.trim()) {
      logger.show_error("Name is required.");
      return;
    }
    try {
      await createStudentCat.mutateAsync({
        classId,
        body: {
          name: vals.name.trim(),
          description: vals.description.trim() || undefined,
        },
      });
      logger.show_success("Student category created.");
      setCatDialogOpen(false);
      catForm.reset();
    } catch (e) {
      logger.show_error(e instanceof Error ? e.message : "Could not create.");
    }
  });

  const saveAttachment = attachForm.handleSubmit(async (vals) => {
    if (!attachForCategory) return;
    if (!vals.componentTypeId) {
      logger.show_error("Select a fee component.");
      return;
    }
    try {
      await createAttach.mutateAsync({
        categoryId: attachForCategory,
        classId,
        body: {
          componentTypeId: vals.componentTypeId,
          ruleType: vals.ruleType,
          value:
            vals.ruleType === "waive"
              ? undefined
              : vals.value.trim() === ""
                ? undefined
                : Number(vals.value),
        },
      });
      logger.show_success("Fee attached to category.");
      setAttachForCategory(null);
      attachForm.reset();
    } catch (e) {
      logger.show_error(e instanceof Error ? e.message : "Could not attach.");
    }
  });

  const loading = cfLoading || compLoading || catLoading;

  return (
    <Box data-testid="class-fee-structure-panel">
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Configure reusable institute fee components, student categories for this
        class, and attach components with pricing rules. Admission flows read the
        aggregate via GET /feeStructure/class-fees.
      </Typography>

      {loading ? (
        <Box display="flex" justifyContent="center" py={4}>
          <CircularProgress />
        </Box>
      ) : null}

      <Card sx={{ mb: 2 }}>
        <CardContent>
          <Typography variant="h6" sx={{ mb: 1 }}>
            Published fee summary
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Read-only view from class-fees (computed amounts per student category).
          </Typography>
          {!classBlock || classBlock.categories.length === 0 ? (
            <Alert severity="info">
              No categories yet — add student categories and attach fee components
              below.
            </Alert>
          ) : (
            classBlock.categories.map((cat) => (
              <Box key={cat.categoryId} sx={{ mb: 2 }}>
                <Typography variant="subtitle1">{cat.category}</Typography>
                <TableContainer component={Paper} variant="outlined">
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Fee</TableCell>
                        <TableCell align="right">Amount</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {cat.feeComponents.map((f) => (
                        <TableRow key={f.feeId}>
                          <TableCell>{f.feeDescription}</TableCell>
                          <TableCell align="right">
                            {formatAmountWithCurrency(f.feeAmount, "INR")}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>
            ))
          )}
        </CardContent>
      </Card>

      <Card sx={{ mb: 2 }}>
        <CardContent>
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            sx={{ mb: 2 }}
          >
            <Typography variant="h6">Institute fee components</Typography>
            <Button
              size="small"
              startIcon={<AddIcon />}
              onClick={() => setFeeDialogOpen(true)}
            >
              Add component
            </Button>
          </Box>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Reusable fee types (tuition, transport, …) for this institute. Attach
            them to student categories below.
          </Typography>
          <TableContainer component={Paper} variant="outlined">
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Name</TableCell>
                  <TableCell>Type</TableCell>
                  <TableCell align="right">Base amount</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {(instituteComponents ?? []).map((c) => (
                  <TableRow key={c.id}>
                    <TableCell>{c.name}</TableCell>
                    <TableCell>{c.feeType}</TableCell>
                    <TableCell align="right">
                      {formatAmountWithCurrency(c.amount, c.currency ?? "INR")}
                    </TableCell>
                    <TableCell align="right">
                      <IconButton
                        size="small"
                        color="error"
                        aria-label="Delete fee component"
                        onClick={async () => {
                          if (
                            !window.confirm(
                              `Delete fee component "${c.name}"? This may break attachments.`,
                            )
                          )
                            return;
                          try {
                            await deleteFeeComp.mutateAsync(c.id);
                            logger.show_success("Fee component deleted.");
                          } catch (e) {
                            logger.show_error(
                              e instanceof Error ? e.message : "Delete failed.",
                            );
                          }
                        }}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>

      <Card>
        <CardContent>
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            sx={{ mb: 2 }}
          >
            <Typography variant="h6">Student categories & attachments</Typography>
            <Button
              size="small"
              startIcon={<AddIcon />}
              onClick={() => setCatDialogOpen(true)}
            >
              Add category
            </Button>
          </Box>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Buckets such as General, SC-ST, Scholarship. Each category can attach
            institute fee components with pricing rules.
          </Typography>

          {(studentCategories ?? []).map((cat) => (
            <Accordion key={cat.id} defaultExpanded={false}>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography sx={{ flex: 1 }}>{cat.name}</Typography>
                <Button
                  size="small"
                  onPointerDown={(ev) => ev.stopPropagation()}
                  onClick={(ev) => {
                    ev.stopPropagation();
                    setAttachForCategory(cat.id);
                  }}
                >
                  Attach fee
                </Button>
                <IconButton
                  size="small"
                  color="error"
                  aria-label="Delete category"
                  onPointerDown={(ev) => ev.stopPropagation()}
                  onClick={async (ev) => {
                    ev.stopPropagation();
                    if (
                      !window.confirm(
                        `Delete student category "${cat.name}"?`,
                      )
                    )
                      return;
                    try {
                      await deleteStudentCat.mutateAsync({
                        id: cat.id,
                        classId,
                      });
                      logger.show_success("Category deleted.");
                    } catch (e) {
                      logger.show_error(
                        e instanceof Error ? e.message : "Delete failed.",
                      );
                    }
                  }}
                >
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </AccordionSummary>
              <AccordionDetails>
                <CategoryAttachmentsSection
                  categoryId={cat.id}
                  classId={classId}
                />
              </AccordionDetails>
            </Accordion>
          ))}

          {(studentCategories ?? []).length === 0 ? (
            <Alert severity="info">No student categories yet.</Alert>
          ) : null}
        </CardContent>
      </Card>

      <Dialog
        open={feeDialogOpen}
        onClose={() => setFeeDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Add institute fee component</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 1, display: "flex", flexDirection: "column", gap: 2 }}>
            <ControlledTextField
              id="fee-comp-name"
              name="name"
              control={feeForm.control}
              label="Name"
              isGridItem={false}
            />
            <ControlledSelectField
              id="fee-comp-type"
              name="feeType"
              control={feeForm.control}
              label="Fee type"
              itemList={FEE_TYPES}
              isRequired
              isGridItem={false}
            />
            <ControlledTextField
              id="fee-comp-amount"
              name="amount"
              control={feeForm.control}
              label="Base amount"
              type="number"
              isGridItem={false}
            />
            <ControlledSelectField
              id="fee-comp-frequency"
              name="frequency"
              control={feeForm.control}
              label="Frequency"
              itemList={FREQUENCIES}
              isRequired
              isGridItem={false}
            />
            <ControlledTextField
              id="fee-comp-desc"
              name="description"
              control={feeForm.control}
              label="Description"
              multiline
              minRows={2}
              isGridItem={false}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setFeeDialogOpen(false)}>Cancel</Button>
          <Button onClick={saveFeeComponent} variant="contained">
            Save
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={catDialogOpen}
        onClose={() => setCatDialogOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Add student category</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 1, display: "flex", flexDirection: "column", gap: 2 }}>
            <ControlledTextField
              id="student-cat-name"
              name="name"
              control={catForm.control}
              label="Name"
              isGridItem={false}
            />
            <ControlledTextField
              id="student-cat-desc"
              name="description"
              control={catForm.control}
              label="Description"
              multiline
              minRows={2}
              isGridItem={false}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCatDialogOpen(false)}>Cancel</Button>
          <Button onClick={saveStudentCategory} variant="contained">
            Save
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={attachForCategory != null}
        onClose={() => setAttachForCategory(null)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Attach fee component</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 1, display: "flex", flexDirection: "column", gap: 2 }}>
            <ControlledSelectField
              id="attach-component"
              name="componentTypeId"
              control={attachForm.control}
              label="Institute fee component"
              itemList={componentOptions}
              isRequired
              isGridItem={false}
            />
            <ControlledSelectField
              id="attach-rule"
              name="ruleType"
              control={attachForm.control}
              label="Rule type"
              itemList={RULE_TYPES}
              isRequired
              isGridItem={false}
            />
            <ControlledTextField
              id="attach-value"
              name="value"
              control={attachForm.control}
              label="Value (amount or percent as applicable; omit for waive)"
              type="number"
              isGridItem={false}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAttachForCategory(null)}>Cancel</Button>
          <Button onClick={saveAttachment} variant="contained">
            Attach
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ClassFeeStructurePanel;
